import {
  FormField,
  Select,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useRealtimeOrgs from '../hooks/useRealtimeOrgs';
import AddOrganizationModal from './addOrganizationModal';

function OrganizationSelect({
  selectedOrgId = null,
  onOrgChange,
  defaultDomain = '',
  label = 'Organization',
  errorText = '',
  disabled = false,
}) {
  const { data: organizations } = useRealtimeOrgs();

  const [openAddOrgModal, setOpenAddOrgModal] = useState(false);

  const orgOptions = useMemo(() => organizations.map((org) => ({
    value: org.id,
    data: org,
    label: `${org.name}${org.domains?.length > 0 ? ` (${org.domains.join(', ')})` : ''} `,
  })), [organizations]);

  const selectedOrg = useMemo(
    () => orgOptions.find((org) => org.value === selectedOrgId),
    [orgOptions, selectedOrgId],
  );

  const lastNotifiedOrgId = useRef(null);
  const handleOrgChange = useCallback((orgData, isManualChange = false) => {
    if (!onOrgChange) return;
    if (lastNotifiedOrgId.current === orgData.id) return;

    lastNotifiedOrgId.current = orgData.id;
    onOrgChange(orgData, isManualChange);
  }, [onOrgChange]);

  // This effect is for handling external changes to selectedOrgId
  useEffect(() => {
    if (lastNotifiedOrgId.current === selectedOrgId) return;
    if (!selectedOrg?.data) return;
    handleOrgChange(selectedOrg.data);
  }, [selectedOrg, handleOrgChange, selectedOrgId]);

  return (
    <>
      <FormField
        label={label}
        errorText={errorText}
        constraintText={!disabled && (
          <span>
            {'Don\'t see the organization you\'re looking for? Add organizations '}
            <a href="#!" onClick={(e) => { e.preventDefault(); setOpenAddOrgModal(true); }}>here</a>
          </span>
        )}
        stretch
      >
        <Select
          disabled={disabled}
          filteringType="auto"
          selectedOption={selectedOrg}
          options={orgOptions}
          onChange={({ detail }) => {
            handleOrgChange(detail.selectedOption.data, true);
          }}
        />
      </FormField>
      {openAddOrgModal && (
        <AddOrganizationModal
          open={openAddOrgModal}
          defaultDomain={defaultDomain}
          onClose={() => setOpenAddOrgModal(false)}
        />
      )}
    </>
  );
}

OrganizationSelect.propTypes = {
  onOrgChange: PropTypes.func.isRequired,
  selectedOrgId: PropTypes.string,
  defaultDomain: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
};

export default OrganizationSelect;
